import React, { useState } from 'react';
import { Button, Tooltip, Drawer, Empty, Space, Tag, Typography, Modal, Select, Input, Spin, Badge } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { notification } from 'antd';

const { TextArea } = Input;

const PAItemFlagIndicator = ({ itemId, userRoleId, initialFlagCount = 0, initialHasFlags = false, compact = false }) => {
  const canCreateFlags = [1, 2].includes(Number(userRoleId));
  const [flags, setFlags] = useState([]);
  const [flagsLoading, setFlagsLoading] = useState(false);
  const [flagsDrawerOpen, setFlagsDrawerOpen] = useState(false);
  const [addFlagModalOpen, setAddFlagModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [dismissModalOpen, setDismissModalOpen] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [flagTypes, setFlagTypes] = useState([]);
  const [newFlagType, setNewFlagType] = useState();
  const [newFlagReason, setNewFlagReason] = useState('');
  const [creatingFlag, setCreatingFlag] = useState(false);
  const [resolvingFlagId, setResolvingFlagId] = useState(0);
  const [resolutionNotes, setResolutionNotes] = useState('');

  const loadFlags = async () => {
    if (!itemId) return;
    setFlagsLoading(true);
    try {
      // Try multiple ID field names as the backend might use different field names
      // Try claim_item_id first (most common), then try item_id, then the raw itemId
      const idParams = [
        { claim_item_id: itemId },
        { item_id: itemId },
        { id: itemId },
        { claim_item_id: String(itemId) },
        { item_id: String(itemId) },
        { id: String(itemId) }
      ];
      
      let active = [];
      let resolved = [];
      
      // Try each parameter format until we get results
      for (const params of idParams) {
        try {
          const [activeResp, resolvedResp] = await Promise.all([
            lib.listPAFlags({ ...params, flag_status: 'active' }),
            lib.listPAFlags({ ...params, flag_status: 'resolved' })
          ]);
          
          const activeData = activeResp?.data?.data || [];
          const resolvedData = resolvedResp?.data?.data || [];
          
          // If we got results, use them and break
          if (activeData.length > 0 || resolvedData.length > 0) {
            active = activeData;
            resolved = resolvedData;
            break;
          }
        } catch (e) {
          // Continue to next parameter format
          continue;
        }
      }
      
      // If still no results, try one more time with just the itemId as claim_item_id
      if (active.length === 0 && resolved.length === 0) {
        try {
          const [activeResp, resolvedResp] = await Promise.all([
            lib.listPAFlags({ claim_item_id: itemId, flag_status: 'active' }).catch(() => ({ data: { data: [] } })),
            lib.listPAFlags({ claim_item_id: itemId, flag_status: 'resolved' }).catch(() => ({ data: { data: [] } }))
          ]);
          active = activeResp?.data?.data || [];
          resolved = resolvedResp?.data?.data || [];
        } catch (e) {
          console.error('Failed to load flags with final attempt:', e);
        }
      }
      
      setFlags([...active, ...resolved]);
    } catch (e) {
      console.error('Failed to load flags:', e);
      // Show error notification if we have initial flags but can't load them
      if (initialHasFlags) {
        notification.warning({
          message: 'Could not load flag details',
          description: 'The flag exists but details could not be loaded. Please try again or contact support.',
          placement: 'topLeft',
          duration: 4
        });
      }
    } finally {
      setFlagsLoading(false);
    }
  };

  const loadFlagTypes = async () => {
    if (flagTypes?.length) return;
    try {
      const resp = await lib.getPAFlagTypes().catch(() => lib.getFlagTypes());
      const list = resp?.data?.data;
      const typesArr = Array.isArray(list) ? list : (list?.pa_item_flag_types || list?.types || []);
      if (Array.isArray(typesArr)) setFlagTypes(typesArr);
    } catch (e) {
      // Non-fatal
    }
  };

  const openFlags = () => {
    setFlagsDrawerOpen(true);
    loadFlags();
    loadFlagTypes();
  };

  const createFlag = async () => {
    if (!newFlagType || !newFlagReason) {
      notification.warning({ message: 'Select flag type and enter reason' });
      return;
    }
    setCreatingFlag(true);
    try {
      const resp = await lib.createPAFlag({
        claim_item_id: itemId,
        flag_type: newFlagType,
        flag_reason: newFlagReason
      });
      if (!resp?.data?.error) {
        notification.success({ message: 'Flag created' });
        setAddFlagModalOpen(false);
        setNewFlagType(undefined);
        setNewFlagReason('');
        await loadFlags();
      } else {
        notification.error({ message: resp?.data?.message || 'Failed to create flag' });
      }
    } catch (e) {
      const msg = e?.response?.status === 409 ? 'Cannot flag item after checkout' : (e?.response?.data?.message || e?.message);
      notification.error({ message: msg });
    } finally {
      setCreatingFlag(false);
    }
  };

  const resolveFlag = (flagId) => {
    const flag = flags.find(f => f.id === flagId);
    setSelectedFlag(flag);
    setResolveModalOpen(true);
  };

  const confirmResolveFlag = async () => {
    setResolvingFlagId(selectedFlag?.id);
    try {
      const resp = await lib.resolvePAFlag(selectedFlag?.id, { resolution_notes: resolutionNotes });
      if (!resp?.data?.error) {
        notification.success({ message: 'Flag resolved' });
        setResolutionNotes('');
        setResolveModalOpen(false);
        setSelectedFlag(null);
        await loadFlags();
      } else {
        notification.error({ message: resp?.data?.message || 'Failed to resolve flag' });
      }
    } catch (e) {
      notification.error({ message: e?.response?.data?.message || e?.message });
    } finally {
      setResolvingFlagId(0);
    }
  };

  const dismissFlag = (flagId) => {
    const flag = flags.find(f => f.id === flagId);
    setSelectedFlag(flag);
    setDismissModalOpen(true);
  };

  const confirmDismissFlag = async () => {
    setResolvingFlagId(selectedFlag?.id);
    try {
      const resp = await lib.dismissPAFlag(selectedFlag?.id, { resolution_notes: resolutionNotes });
      if (!resp?.data?.error) {
        notification.success({ message: 'Flag dismissed' });
        setResolutionNotes('');
        setDismissModalOpen(false);
        setSelectedFlag(null);
        await loadFlags();
      } else {
        notification.error({ message: resp?.data?.message || 'Failed to dismiss flag' });
      }
    } catch (e) {
      notification.error({ message: e?.response?.data?.message || e?.message });
    } finally {
      setResolvingFlagId(0);
    }
  };

  // Use loaded flags if available, otherwise use initial data
  const displayFlagCount = flags.length > 0 ? flags.length : (initialHasFlags ? initialFlagCount : 0);
  const hasFlags = displayFlagCount > 0;
  const safeFlagCount = Number(displayFlagCount) || 0;

  return (
    <>
      <Tooltip title={hasFlags ? `${displayFlagCount} flag(s)` : canCreateFlags ? 'Click to view/add flags' : 'Click to view flags'}>
        <Button
          type={compact ? 'text' : (hasFlags ? 'primary' : 'default')}
          size={compact ? 'small' : 'middle'}
          className={compact ? 'pa-batch-flag-action' : undefined}
          onClick={openFlags}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          {hasFlags ? (
            <Badge count={safeFlagCount} showZero={false} offset={[10, 0]}>
              <WarningOutlined style={{ color: hasFlags ? '#fff' : '#faad14' }} />
            </Badge>
          ) : (
            <WarningOutlined style={{ color: '#faad14' }} />
          )}
        </Button>
      </Tooltip>

        <Drawer
          open={flagsDrawerOpen}
          onClose={() => setFlagsDrawerOpen(false)}
          width={520}
          title={
            <Typography.Title level={4} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <WarningOutlined style={{ color: '#faad14' }} />
              Flags
            </Typography.Title>
          }
          footer={
            canCreateFlags ? [
              <Button key="add" type="primary" icon={<WarningOutlined />} onClick={() => setAddFlagModalOpen(true)}>
                Add Flag
              </Button>
            ] : null
          }
        >
          <Spin spinning={flagsLoading}>
            {(!flags || flags.length === 0) ? (
              <Empty description="No flags for this item" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size={16}>
                {flags.map((f) => (
                  <div
                    style={{
                      border: `2px solid ${f.flag_status === 'active' ? '#ff4d4f' : f.flag_status === 'resolved' ? '#52c41a' : '#d9d9d9'}`,
                      borderRadius: 12,
                      padding: 16,
                      backgroundColor: f.flag_status === 'active' ? '#fff1f0' : f.flag_status === 'resolved' ? '#f6ffed' : '#fafafa',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => { setSelectedFlag(f); setDetailsModalOpen(true); }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    key={f.id}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <Space size={8} wrap>
                        <Tag color={f.flag_status === 'active' ? 'red' : f.flag_status === 'resolved' ? 'green' : 'default'} style={{ fontWeight: 600, fontSize: 12 }}>
                          {f.flag_status?.toUpperCase?.() || f.flag_status}
                        </Tag>
                        <Tag color="blue" style={{ fontWeight: 500, fontSize: 12 }}>
                          {String(f.flag_type || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </Tag>
                      </Space>
                      <Space>
                        {f.flag_status === 'active' && canCreateFlags && (
                          <>
                            <Button size="small" loading={resolvingFlagId === f.id} onClick={(e) => { e.stopPropagation(); resolveFlag(f.id); }} type="primary">
                              Resolve
                            </Button>
                            <Button size="small" danger loading={resolvingFlagId === f.id} onClick={(e) => { e.stopPropagation(); dismissFlag(f.id); }}>
                              Dismiss
                            </Button>
                          </>
                        )}
                      </Space>
                    </div>
                    <div style={{ fontSize: 14, lineHeight: 1.6, color: '#595959' }}>{f.flag_reason || f.message || 'No reason provided'}</div>
                    {f.metadata && Object.keys(f.metadata).length > 0 && (
                      <div style={{ marginTop: 12, padding: 12, background: 'white', borderRadius: 8, border: '1px solid #e8e8e8' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#8c8c8c', marginBottom: 6 }}>METADATA</div>
                        <pre style={{ margin: 0, fontSize: 12, lineHeight: 1.5, overflow: 'auto', maxHeight: 120 }}>
                          {JSON.stringify(f.metadata, null, 2)}
                        </pre>
                      </div>
                    )}
                    {f.flagged_at && (
                      <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 8 }}>
                        Created: {new Date(f.flagged_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                ))}
              </Space>
            )}
          </Spin>
        </Drawer>

        <Modal open={addFlagModalOpen} onCancel={() => setAddFlagModalOpen(false)} onOk={createFlag} okButtonProps={{ loading: creatingFlag }} title="Add Flag" destroyOnClose>
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label className="form-label">Flag Type</label>
            <Select
              style={{ width: '100%' }}
              value={newFlagType}
              onChange={setNewFlagType}
              options={(flagTypes?.length ? flagTypes : ['cost_discrepancy', 'duplicate_item', 'suspicious_pattern', 'document_missing', 'manual_review', 'fraud_risk', 'policy_violation']).map(t => ({
                value: t,
                label: String(t).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
              }))}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label className="form-label">Reason</label>
            <TextArea rows={3} value={newFlagReason} onChange={(e) => setNewFlagReason(e.target.value)} />
          </div>
        </Modal>

        <Modal
          open={resolveModalOpen}
          onCancel={() => { setResolveModalOpen(false); setResolutionNotes(''); setSelectedFlag(null); }}
          onOk={confirmResolveFlag}
          okButtonProps={{ loading: resolvingFlagId > 0 }}
          title="Resolve Flag"
          destroyOnClose
        >
          <Typography.Paragraph>Resolve this flag? Add notes explaining the resolution.</Typography.Paragraph>
          <TextArea rows={4} value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)} placeholder="Enter resolution notes..." />
        </Modal>

        <Modal
          open={dismissModalOpen}
          onCancel={() => { setDismissModalOpen(false); setResolutionNotes(''); setSelectedFlag(null); }}
          onOk={confirmDismissFlag}
          okButtonProps={{ loading: resolvingFlagId > 0, danger: true }}
          title="Dismiss Flag"
          destroyOnClose
        >
          <Typography.Paragraph>Dismiss this flag as not applicable? Add notes explaining why.</Typography.Paragraph>
          <TextArea rows={4} value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)} placeholder="Enter dismissal notes..." />
        </Modal>

        <Modal
          open={detailsModalOpen}
          onCancel={() => { setDetailsModalOpen(false); setSelectedFlag(null); }}
          footer={[
            <Button key="close" onClick={() => { setDetailsModalOpen(false); setSelectedFlag(null); }}>Close</Button>,
            ...(selectedFlag?.flag_status === 'active' && canCreateFlags ? [
              <Button key="resolve" type="primary" loading={resolvingFlagId > 0} onClick={() => { setDetailsModalOpen(false); resolveFlag(selectedFlag.id); }}>
                Resolve
              </Button>,
              <Button key="dismiss" danger loading={resolvingFlagId > 0} onClick={() => { setDetailsModalOpen(false); dismissFlag(selectedFlag.id); }}>
                Dismiss
              </Button>
            ] : [])
          ]}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <WarningOutlined style={{ color: '#faad14' }} />
              Flag Details
            </div>
          }
          destroyOnClose
          width={700}
        >
          {selectedFlag && (
            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <Space direction="vertical" style={{ width: '100%' }} size={16}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#8c8c8c', marginBottom: 8 }}>STATUS & TYPE</div>
                  <Space size={8} wrap>
                    <Tag color={selectedFlag.flag_status === 'active' ? 'red' : selectedFlag.flag_status === 'resolved' ? 'green' : 'default'} style={{ fontWeight: 600, fontSize: 12 }}>
                      {selectedFlag.flag_status?.toUpperCase?.() || selectedFlag.flag_status}
                    </Tag>
                    <Tag color="blue" style={{ fontWeight: 500, fontSize: 12 }}>
                      {String(selectedFlag.flag_type || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </Tag>
                  </Space>
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#8c8c8c', marginBottom: 8 }}>REASON</div>
                  <div style={{ padding: 12, background: '#fafafa', borderRadius: 8, border: '1px solid #e8e8e8' }}>
                    {selectedFlag.flag_reason || selectedFlag.message || 'No reason provided'}
                  </div>
                </div>
                {selectedFlag.metadata && Object.keys(selectedFlag.metadata).length > 0 && (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#8c8c8c', marginBottom: 8 }}>METADATA</div>
                    <div style={{ padding: 12, background: '#fafafa', borderRadius: 8, border: '1px solid #e8e8e8' }}>
                      <pre style={{ margin: 0, fontSize: 12, lineHeight: 1.5 }}>{JSON.stringify(selectedFlag.metadata, null, 2)}</pre>
                    </div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#8c8c8c', marginBottom: 8 }}>TIMELINE</div>
                  <div style={{ padding: 12, background: '#fafafa', borderRadius: 8, border: '1px solid #e8e8e8' }}>
                    <Space direction="vertical" style={{ width: '100%' }} size={8}>
                      {selectedFlag.flagged_at && <div><span style={{ fontWeight: 600 }}>Created:</span> {new Date(selectedFlag.flagged_at).toLocaleString()}</div>}
                      {selectedFlag.resolved_at && <div><span style={{ fontWeight: 600 }}>Resolved:</span> {new Date(selectedFlag.resolved_at).toLocaleString()}</div>}
                      {selectedFlag.flagged_by && <div><span style={{ fontWeight: 600 }}>Flagged By:</span> User ID {selectedFlag.flagged_by}</div>}
                      {selectedFlag.resolved_by && <div><span style={{ fontWeight: 600 }}>Resolved By:</span> User ID {selectedFlag.resolved_by}</div>}
                    </Space>
                  </div>
                </div>
                {selectedFlag.resolution_notes && (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#8c8c8c', marginBottom: 8 }}>RESOLUTION NOTES</div>
                    <div style={{ padding: 12, background: '#f6ffed', borderRadius: 8, border: '1px solid #b7eb8f' }}>
                      {selectedFlag.resolution_notes}
                    </div>
                  </div>
                )}
              </Space>
            </div>
          )}
        </Modal>
      </>
    );
};

export default PAItemFlagIndicator;

